import { useState } from "react";
import { ErrorBoundary } from "react-error-boundary";

import { getIsThreeDMapsPreferred, type MapType, useAppSelector } from "@lib/store";

import { MapiqMapContent } from "./components/MapiqMapContent";
import { MapiqMapError } from "./components/MapiqMapError";
import type { MapLibraryProps } from "./types";

export type MapiqMapProps = MapLibraryProps & {
  buildingId: string;
  floorId: string;
  type?: MapType | "preference";
  onError?: (error: Error) => void;
};

/** The map type to load: "preference" follows the user's 2D or 3D setting. */
const resolveMapType = (type: MapType | "preference", isThreeDPreferred: boolean): MapType => {
  if (type !== "preference") return type;

  return isThreeDPreferred ? "3d" : "2d";
};

/**
 * A floor map on the `mapiq-map-react` library: loads the floor's map and renderer. `mapViewApiRef` moves the view,
 * so a handler that selects something can bring it into view. It's `null` until the floor's map is built.
 */
export const MapiqMap = ({ buildingId, floorId, type = "2d", onError, ...props }: MapiqMapProps) => {
  const isThreeDPreferred = useAppSelector(getIsThreeDMapsPreferred);
  const mapType = resolveMapType(type, isThreeDPreferred);
  const [hasSize, setHasSize] = useState(false);

  // The library builds the map straight away and fixes a 2D map's width then, so it waits until there's room for it
  const measureContainer = (container: HTMLDivElement) => {
    const observer = new ResizeObserver(([entry]) => {
      setHasSize(!!entry && entry.contentRect.width > 0 && entry.contentRect.height > 0);
    });

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  };

  return (
    <div
      ref={measureContainer}
      className="size-full">
      <ErrorBoundary
        resetKeys={[buildingId, floorId, mapType]}
        onError={onError}
        FallbackComponent={MapiqMapError}>
        <MapiqMapContent
          key={`${floorId}|${mapType}`}
          buildingId={buildingId}
          floorId={floorId}
          mapType={mapType}
          hasSize={hasSize}
          {...props}
        />
      </ErrorBoundary>
    </div>
  );
};
